import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/test-system.css';
import './TestApplication.css';
import Navigation from '../components/Navigation';
import TestNav from '../components/tests/TestNav';
import { getTestApplication, readApiError, submitTestApplication } from '../api/testSystem';
import { isAuthenticated } from '../utils/auth';
import { formatToLocalDate } from '../utils/dateUtils';

const AlertIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const CheckIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const ClockIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

const MULTI_CHOICE = 'multi_choice';

/* The three things that happen, in the order they happen — the same sentences
   the form and its two after-states already say, set beside them so a candidate
   can see where they are in it. */
const STEPS = [
    {
        key: 'apply',
        title: 'Send your application',
        text: 'Tell us about yourself once. Answers cannot be changed after you send them.',
    },
    {
        key: 'review',
        title: 'The team reads it',
        text: 'The team reads every application. You get an email when yours is approved.',
    },
    {
        key: 'catalog',
        title: 'The catalog opens',
        text: 'The tests you can take appear in the catalog on their own.',
    },
];

/* How many of the steps are behind the candidate, per application state. */
const STEPS_DONE = { pending: 1, approved: STEPS.length };

const ApplicationSteps = ({ state }) => {
    const done = STEPS_DONE[state] || 0;

    return (
        <aside className="ts-application-aside" aria-labelledby="application-steps-title">
            <h2 className="ts-panel-title" id="application-steps-title">How it works</h2>
            <ol className="ts-steps" data-testid="application-steps">
                {STEPS.map((step, index) => {
                    const status = index < done ? 'done' : index === done ? 'current' : 'next';

                    return (
                        <li
                            className={`ts-step ts-step--${status}`}
                            key={step.key}
                            aria-current={status === 'current' ? 'step' : undefined}
                        >
                            <span className="ts-step-mark" aria-hidden="true">
                                {status === 'done' ? <CheckIcon /> : index + 1}
                            </span>
                            <div className="ts-step-body">
                                <p className="ts-step-title">
                                    {step.title}
                                    {status === 'done' && <span className="ts-step-sr"> — done</span>}
                                </p>
                                <p className="ts-step-text">{step.text}</p>
                            </div>
                        </li>
                    );
                })}
            </ol>
        </aside>
    );
};

/* An unanswered question, whatever its type: the empty string a text field
   leaves behind, the empty list a checkbox group leaves behind, or nothing at
   all. A number of 0 is an answer. */
const isBlank = (value) =>
    value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

const sortQuestions = (form) =>
    [...(form?.questions || [])]
        .filter(question => question?.question_id)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const Question = ({ question, value, invalid, onChange }) => {
    const { question_id: id, type, label, help, required } = question;
    const options = question.options || [];
    const describedBy = [help && `${id}-help`, invalid && `${id}-error`].filter(Boolean).join(' ') || undefined;
    const inputProps = {
        id,
        value: value ?? '',
        onChange: (event) => onChange(id, event.target.value),
        'aria-invalid': invalid ? 'true' : undefined,
        'aria-describedby': describedBy,
        'data-testid': `answer-${id}`,
    };

    let control;
    if (type === 'long_text') {
        control = <textarea className="ts-input ts-textarea" rows={5} {...inputProps} />;
    } else if (type === 'number') {
        control = <input className="ts-input" type="number" {...inputProps} />;
    } else if (type === 'url') {
        control = <input className="ts-input" type="url" placeholder="https://" {...inputProps} />;
    } else if (type === 'single_choice' || type === MULTI_CHOICE) {
        const multi = type === MULTI_CHOICE;
        const chosen = multi ? (Array.isArray(value) ? value : []) : value;

        control = (
            <div className="ts-choices" data-testid={`answer-${id}`}>
                {options.map((option) => {
                    const checked = multi ? chosen.includes(option) : chosen === option;

                    return (
                        <label className={`ts-choice${checked ? ' ts-choice--on' : ''}`} key={option}>
                            <input
                                type={multi ? 'checkbox' : 'radio'}
                                name={id}
                                value={option}
                                checked={checked}
                                aria-describedby={describedBy}
                                onChange={() => onChange(
                                    id,
                                    multi
                                        ? (checked ? chosen.filter(item => item !== option) : [...chosen, option])
                                        : option,
                                )}
                            />
                            <span>{option}</span>
                        </label>
                    );
                })}
            </div>
        );
    } else {
        // Anything else, including a type this build has not been taught yet, is
        // answered as a line of text rather than not answered at all.
        control = <input className="ts-input" type="text" {...inputProps} />;
    }

    const labelled = type === 'single_choice' || type === MULTI_CHOICE;

    return (
        <div
            className={`ts-field${invalid ? ' ts-field--invalid' : ''}`}
            role={labelled ? 'group' : undefined}
            aria-labelledby={labelled ? `${id}-label` : undefined}
            data-testid={`question-${id}`}
        >
            <label className="ts-label" id={`${id}-label`} htmlFor={labelled ? undefined : id}>
                {label || 'Question'}
                {!required && <span className="ts-optional">Optional</span>}
            </label>
            {help && <p className="ts-help" id={`${id}-help`}>{help}</p>}
            {control}
            {invalid && (
                <p className="ts-field-error" id={`${id}-error`} data-testid={`error-${id}`}>
                    This one is required.
                </p>
            )}
        </div>
    );
};

const TestApplication = () => {
    const navigate = useNavigate();

    const [form, setForm] = useState(null);
    const [application, setApplication] = useState(null);
    const [answers, setAnswers] = useState({});
    const [missing, setMissing] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const loadApplication = useCallback(async () => {
        setLoading(true);
        setLoadError('');

        try {
            const data = await getTestApplication();
            setForm(data.form);
            setApplication(data.application);
        } catch (e) {
            setLoadError(readApiError(e, 'The application could not be loaded.').message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/login');
            return;
        }

        loadApplication();
    }, [navigate, loadApplication]);

    const questions = useMemo(() => sortQuestions(form), [form]);

    const handleChange = (questionId, value) => {
        setAnswers(current => ({ ...current, [questionId]: value }));
        setMissing(current => current.filter(id => id !== questionId));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitError('');

        const unanswered = questions
            .filter(question => question.required && isBlank(answers[question.question_id]))
            .map(question => question.question_id);

        if (unanswered.length > 0) {
            setMissing(unanswered);
            setSubmitError('Answer the highlighted questions, then send it.');
            return;
        }

        setSubmitting(true);

        // Blanks are left out entirely: an unanswered optional question is absent,
        // not an empty answer stored against a question_id. A number question is
        // stored as a number, not as the string the input held.
        const payload = Object.fromEntries(
            questions
                .map(question => [question.question_id, answers[question.question_id], question.type])
                .filter(([, value]) => !isBlank(value))
                .map(([id, value, type]) => [
                    id,
                    type === 'number' && Number.isFinite(Number(value)) ? Number(value) : value,
                ]),
        );

        try {
            setApplication(await submitTestApplication(payload));
        } catch (e) {
            const { message, code } = readApiError(e, 'Your application could not be sent.');

            if (code === 'already_applied') {
                // A second tab, or a second click that beat the first one home —
                // whatever the backend holds is the application.
                loadApplication();
            } else if (code === 'missing_answers') {
                setMissing(e?.response?.data?.missing_question_ids || []);
                setSubmitError(message);
            } else {
                setSubmitError(message);
            }
        }

        setSubmitting(false);
    };

    /* The backend never tells a candidate their application was turned down: every
       state that is not approved arrives here as pending, and this page shows only
       those two. */
    const state = application?.state;
    /* The steps stand beside a form to fill in or an application already sent —
       not beside a page that is still loading, failed, or has no form to offer. */
    const showSteps = !loading && !loadError && (state === 'approved' || state === 'pending' || Boolean(form));

    const renderBody = () => {
        if (loading) {
            return (
                <div className="ts-page-state ts-page-state--loading" data-testid="application-loading">
                    <div className="ts-spinner" />
                    <p>Loading your application…</p>
                </div>
            );
        }

        if (loadError) {
            return (
                <div className="ts-page-state" role="alert">
                    <h2>The application is unavailable</h2>
                    <p>{loadError}</p>
                    <button className="ts-btn" onClick={loadApplication}>Try again</button>
                </div>
            );
        }

        if (state === 'approved') {
            return (
                <section className="ts-panel ts-application-state" data-testid="application-approved">
                    <span className="ts-chip ts-chip--ok">
                        <CheckIcon />
                        Approved
                    </span>
                    <h2 className="ts-application-state-title">You're in</h2>
                    <p className="ts-application-state-copy">
                        Your application was approved
                        {application.approved_at ? ` on ${formatToLocalDate(application.approved_at)}` : ''}.
                        Every cycle you can take is in the catalog — enrol with an entry, or buy one
                        at checkout.
                    </p>
                    <div className="ts-application-actions">
                        <Link className="ts-btn ts-btn--primary" to="/tests">Browse the catalog</Link>
                        <Link className="ts-link" to="/tests/entries">Get entries</Link>
                    </div>
                </section>
            );
        }

        if (state === 'pending') {
            return (
                <section className="ts-panel ts-application-state" data-testid="application-pending">
                    <span className="ts-chip">
                        <ClockIcon />
                        With the team
                    </span>
                    <h2 className="ts-application-state-title">Your application is in</h2>
                    <p className="ts-application-state-copy">
                        We got it
                        {application.submitted_at ? ` on ${formatToLocalDate(application.submitted_at)}` : ''}.
                        The team reads every application. You'll get an email when yours is
                        approved, and the tests you can take appear in the catalog on their own —
                        there is nothing else to send.
                    </p>
                    <div className="ts-application-actions">
                        <Link className="ts-link" to="/tests">Back to the catalog</Link>
                    </div>
                </section>
            );
        }

        if (!form) {
            return (
                <div className="ts-page-state" data-testid="application-closed">
                    <h2>Applications are not open right now</h2>
                    <p>
                        There is no application form to fill in at the moment. Check back later —
                        this page is where it will be.
                    </p>
                </div>
            );
        }

        return (
            <form className="ts-panel ts-application-form" onSubmit={handleSubmit} noValidate data-testid="application-form">
                <div className="ts-fields">
                    {questions.map(question => (
                        <Question
                            key={question.question_id}
                            question={question}
                            value={answers[question.question_id]}
                            invalid={missing.includes(question.question_id)}
                            onChange={handleChange}
                        />
                    ))}
                </div>

                {submitError && (
                    <p className="ts-note ts-note--danger" role="alert" data-testid="application-error">
                        <AlertIcon />
                        <span>{submitError}</span>
                    </p>
                )}

                <div className="ts-application-submit">
                    <button
                        className="ts-btn ts-btn--primary"
                        type="submit"
                        disabled={submitting}
                        data-testid="application-submit"
                    >
                        {submitting && <span className="ts-spinner-sm" />}
                        Send application
                    </button>
                    <p className="ts-application-once">
                        You apply once — answers cannot be changed after you send them.
                    </p>
                </div>
            </form>
        );
    };

    return (
        <>
            <Navigation />
            <div className="ts-page">
                <div className="ts-shell ts-shell--wide">
                    <TestNav />

                    <header className="ts-head">
                        <div className="ts-head-main">
                            <h1 className="ts-title">Application</h1>
                            <p className="ts-lede">
                                Tests are open to approved candidates. Tell us about yourself once; the
                                team reviews it and the catalog opens up from there.
                            </p>
                        </div>
                    </header>

                    {showSteps ? (
                        <div className="ts-application-grid">
                            <div className="ts-application-main">{renderBody()}</div>
                            <ApplicationSteps state={state} />
                        </div>
                    ) : renderBody()}
                </div>
            </div>
        </>
    );
};

export default TestApplication;
